import type { MapConfig } from '../api/types'

/**
 * Woher die Kartenkacheln kommen — gemerkt im Gerät.
 *
 * Die Quelle steht in der `.env` des Servers und kommt über `/api/settings`.
 * Der Grund dafür ist ein praktischer: OpenTopoMap ist ein Freizeitprojekt und
 * sagt keine Ausfallsicherheit zu. Bleibt die Karte eines Tages grau, soll eine
 * geänderte Zeile per FTP genügen — nicht ein neuer Bau der Oberfläche,
 * während jemand unterwegs ist.
 *
 * **Genau daran hing aber ein Haken.** Die Karte steht schon, wenn die Antwort
 * auf `/api/settings` noch unterwegs ist; ohne einen Wert für diese Sekunde
 * bliebe sie leer. Der Notnagel im Code nennt aber OpenTopoMap — und wer die
 * `.env` umstellt, *weil* dieser Server nicht mehr antwortet, dessen Leser
 * griffen bei jedem Öffnen weiter erst einmal an die tote Adresse. Der
 * Schalter wirkte, nur nicht sauber.
 *
 * Deshalb wird die zuletzt genannte Quelle hier behalten. Nach dem ersten
 * Öffnen mit der neuen `.env` fragt das Gerät die alte Adresse nie wieder an.
 *
 * Im Gerät und nicht auf dem Server, wie bei `letzterChat.ts`: es ist kein
 * Wissen über den Benutzer, sondern die Erinnerung dieses einen Browsers
 * daran, was beim letzten Mal galt.
 */
/**
 * Womit eine Kachelebene gebaut wird — **an einer Stelle**, für beide Karten.
 *
 * Die Reisekarte und die kleine Karte im Buch bauten ihre Ebene je selbst; als
 * die Kachelnutzung eine Angabe mehr verlangte, wäre sie sonst an einer von
 * beiden vergessen worden.
 *
 * **`referrerPolicy` ist die Angabe, um die es geht.** Aus dem Betrieb, auf
 * einem Android-Gerät: statt der Karte stand auf jeder Kachel „Access blocked —
 * Referer is required by tile usage policy of OpenStreetMap's volunteer-run
 * servers". Die Anwendung schickt aus gutem Grund `Referrer-Policy:
 * same-origin` (`server/.htaccess`) — nach außen ging damit **gar nichts**, und
 * genau das lehnt der Kachelserver ab.
 *
 * Hier steht deshalb die Ausnahme, und sie ist so eng wie möglich: **nur die
 * Kacheln**, und `strict-origin-when-cross-origin` schickt dorthin **nur den
 * Namen dieser Seite**, nie die Adresse der Reise. Ein Merkmal am Element
 * überstimmt die Angabe der Seite; alles andere, was der Browser selbst
 * abruft (die Vorschaubildchen der GIF-Auswahl), bleibt ohne Absender.
 *
 * Was damit hinausgeht, steht in `docs/datenschutz-leser.md` — dort, wo die
 * erste Ausnahme erklärt ist.
 */
export function kacheloptionen(quelle: MapConfig) {
  return {
    maxZoom: quelle.max_zoom,
    // Pflicht und keine Höflichkeit: die Kacheln gehören anderen, und
    // CC-BY-SA verlangt die Nennung sichtbar an der Karte.
    attribution: quelle.attribution,
    // `as const`, damit der genaue Wert stehen bleibt: Leaflets Typ nimmt
    // eine feste Auswahl an Wörtern, und ein blosses `string` passt darauf
    // nicht.
    referrerPolicy: 'strict-origin-when-cross-origin' as const,
  }
}

const MARKE = 'tb.kartenquelle'

/**
 * Gelesen **beim Laden des Moduls**, also vor dem ersten Zeichnen.
 *
 * Derselbe Grund wie bei `letzterChat.ts`: gelesen wird einmal und früh, damit
 * das Ergebnis nicht davon abhängt, welcher Effekt zuerst drankommt.
 */
let beimStart = lies()

function lies(): MapConfig | null {
  try {
    const roh = localStorage.getItem(MARKE)
    if (roh === null) return null
    const wert = JSON.parse(roh) as unknown
    return pruefe(wert)
  } catch {
    // Ein verbogener Eintrag ist kein Grund, keine Karte zu zeigen -- dann
    // gilt eben die eingebaute Vorgabe.
    return null
  }
}

/**
 * Geprüft wird auch beim Lesen.
 *
 * Was hier steht, geht ungefragt als Adresse in den Browser. Ein alter oder
 * verbogener Eintrag hat dort nichts zu suchen, und `https` ist keine
 * Förmlichkeit: die Anwendung selbst läuft verschlüsselt, und eine Kachel über
 * `http` machte daraus in jedem Browser eine Warnung.
 */
function pruefe(wert: unknown): MapConfig | null {
  if (typeof wert !== 'object' || wert === null) return null
  const { tile_url, attribution, max_zoom } = wert as Record<string, unknown>
  if (typeof tile_url !== 'string' || !tile_url.startsWith('https://')) return null
  if (typeof attribution !== 'string' || attribution === '') return null
  if (typeof max_zoom !== 'number' || !Number.isFinite(max_zoom)) return null
  if (max_zoom < 1 || max_zoom > 22) return null
  return { tile_url, attribution, max_zoom }
}

export function gemerkteQuelle(): MapConfig | null {
  return beimStart
}

export function merkeQuelle(quelle: MapConfig): void {
  const geprueft = pruefe(quelle)
  if (geprueft === null) return
  beimStart = geprueft
  try {
    localStorage.setItem(MARKE, JSON.stringify(geprueft))
  } catch {
    // Nicht merken zu können ist kein Grund, keine Karte zu zeigen.
  }
}
